import { PATCH_USER_ENDPOINT, PATCH_USER_PROFILE_ENDPOINT, USER_EXISTING_ENDPOINT, USER_METADATA_ENDPOINT, USER_NEW_ENDPOINT } from '../config/api-endpoints'
import { authorizeHeader } from '../utils/auth-header'
import AuthService from '../services/AuthService'
import { getToggleData } from '../utils/helpers'

export const patchNewUserAPICall = async (userId, { user, appMetadata, userMetadata }) => {
  try {
    let payloadToPass = { ...user, ...appMetadata, ...userMetadata }
    const config = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payloadToPass)
    }
    const response = await window.fetch(USER_NEW_ENDPOINT + '/' + encodeURIComponent(userId), authorizeHeader(config))
    const responseOk = await response.status === 200
    const responseJSON = await response.json()
    return { responseOk, responseJSON }
  } catch (e) {
    console.error('ERROR: ', e)
    return e
  }
}

export const patchExistingUserAPICall = async (userId, { user, appMetadata, userMetadata }) => {
  try {
    const auth = new AuthService()
    let userProfile = auth.getProfileRequestBodyForUpdate()
    const toggleData = getToggleData(userProfile?.user_metadata?.user_type)
    let payloadToPass = { ...user, ...appMetadata, ...userMetadata }

     payloadToPass = {
      ...payloadToPass,
      app_metadata: {
        ...payloadToPass.app_metadata,
        ...toggleData
      }
    }

    if (userId.startsWith('google-oauth2|')) {
      payloadToPass.connection = 'google-oauth2'
    }
    
    const config = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payloadToPass)
    }
    const response = await window.fetch(USER_EXISTING_ENDPOINT + '/' + encodeURIComponent(userId), authorizeHeader(config))
    const responseOk = await response.status === 200
    const responseJSON = await response.json()
    return { responseOk, responseJSON }
  } catch (e) {
    console.error('ERROR: ', e)
    return e
  }
}

export const patchUserMetadataApiCall = async (requestBody, maxRetries = 3) => {
  const config = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  }

  let lastError = null;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await window.fetch(USER_METADATA_ENDPOINT, authorizeHeader(config))

      const contentType = response.headers.get('content-type')
      if (!contentType?.includes('application/json')) {
        lastError = {
          success: false,
          error: 'Response is not JSON',
          responseStatus: response.status
        }
        continue;
      }

      const responseJSON = await response.json()
      
      if (!response.ok) {
        lastError = {
          success: false,
          error: responseJSON.message || `HTTP error! status: ${response.status}`,
          responseStatus: response.status,
          responseJSON
        }
        // Don't retry on client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          return lastError;
        }
        continue;
      }

      return {
        success: true,
        responseStatus: response.status,
        responseJSON
      }

    } catch (error) {
      console.error(`Attempt ${attempt + 1} failed to patch user metadata:`, error.message, 'Request body:', JSON.stringify(requestBody))
      lastError = {
        success: false,
        error: error.message,
        responseStatus: 500
      }
      
      // Wait before retrying with exponential backoff
      if (attempt < maxRetries - 1) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 10000); // Max 10 second delay
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  return lastError;
}


export const getNewAccessTokenUserAPICall = async (userId, { user, appMetadata, userMetadata }) => {
  try {
    let payloadToPass = { ...user, ...appMetadata, ...userMetadata }
    const config = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payloadToPass)
    }
    const response = await window.fetch(USER_EXISTING_ENDPOINT + '/' + encodeURIComponent(userId), authorizeHeader(config))
    const responseOk = await response.status === 200
    const responseJSON = await response.json()
    return { responseOk, responseJSON }
  } catch (e) {
    console.error('ERROR: ', e)
    return e
  }
}

export const patchUserProfileApiCall = async (userId, requestBody) => {
  try {
    const config = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    }
    const response = await window.fetch(PATCH_USER_PROFILE_ENDPOINT, authorizeHeader(config))
    const responseStatus = await response.status
    let responseJSON;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      responseJSON = await response.json()
    } else {
      throw Error(`Response is not a JSON.`);
    }
    return { responseStatus, responseJSON }
  } catch (e) {
    console.error(e)
    return e
  }
}

export const postCreateUserAPICall = async (requestData) => {
  try {
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    }
    const response = await window.fetch(USER_EXISTING_ENDPOINT, authorizeHeader(config))
    const responseOk = await response.status === 201
    const responseJSON = await response.json()
    return { responseOk, responseJSON }
  } catch (e) {
    console.error('ERROR: ', e)
    return e
  }
}

export const patchUserUpdateApiCall = async (userId, requestBody) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const config = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    };

    const response = await window.fetch(PATCH_USER_ENDPOINT + userId, authorizeHeader(config));
    const responseStatus = response.status;
    
    if (responseStatus >= 400) {
      const errorText = await response.text();
      throw new Error(`API Error (${responseStatus}): ${errorText}`);
    }

    let responseJSON = null;
    const contentType = response.headers.get("content-type");
    
    if (contentType && contentType.indexOf("application/json") !== -1) {
      responseJSON = await response.json();
    } else if (responseStatus !== 204) { // No content is acceptable
      console.warn(`Response is not JSON. Content-Type: ${contentType}`);
    }

    return { responseStatus, responseJSON };
  } catch (e) {
    console.error('Error in patchUserUpdateApiCall:', e);
    return { 
      responseStatus: 500, 
      responseJSON: null, 
      error: e.message || 'Unknown error occurred'
    };
  }
}


export const getUpdatedUserApiCall = async () => {
  const authService = new AuthService()
  const profile = authService.getUserProfile()
  try {
    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }
    const response = await window.fetch(PATCH_USER_ENDPOINT + profile?.sub, authorizeHeader(config))
    const responseStatus = await response.status
    let responseJSON;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      responseJSON = await response.json()
    } else {
      throw Error(`Response is not a JSON.`);
    }
    if (responseStatus === 200) {
      return responseJSON
    }
  } catch (e) {
    console.log(e)
    // return e
  }
}
// https://alemhealthapi.azure-api.net/teleradsetting/api/users/auth0|64a268dff8b779439161c5ab